/**
 * Scene preview tile.
 *
 * Renders a live, animated preview of a scene:
 *   - procedural scenes are drawn by the same canvas code that exports use
 *   - imported clips are played back in a muted <video> element
 *
 * Uses IntersectionObserver so off-screen cards stop animating - on a
 * phone with 16 scene cards open, that is the difference between smooth
 * and unusable.
 */

import { h } from '../../utils/dom.js';
import { resolveRenderer } from '../../scenes/procedural/index.js';
import { SILENT_AUDIO } from '../../scenes/procedural/shared.js';

export function createScenePreview(scene, { width = 264, height = 470, animated = true, strength = 0, getFeatures = null } = {}) {
  const { renderer } = resolveRenderer(scene, { allowFallback: true });
  const container = h('div', { class: 'scene-card__thumb' });

  let canvas = null;
  let video = null;
  let rafId = null;
  let running = false;
  let visible = true;
  let startedAt = performance.now();
  const loopSeconds = Math.max(2, Number(scene?.duration) || 10);

  if (scene?.assetType === 'video' && scene.assetUrl) {
    video = h('video', {
      src: scene.assetUrl,
      muted: true,
      loop: true,
      playsInline: true,
      autoplay: true,
      preload: 'metadata',
      'webkit-playsinline': '',
    });
    container.append(video);
  } else if (renderer) {
    canvas = h('canvas', { width, height });
    container.append(canvas);
    drawOnce(0);
  } else {
    container.append(
      h('div', {
        class: 'stack',
        style: {
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '12px',
          background: '#0e1015',
        },
      }, [
        h('div', { class: 'tiny dim' }, ['No asset yet']),
      ]),
    );
  }

  function drawOnce(t) {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const features = getFeatures ? getFeatures() : SILENT_AUDIO;
    try {
      renderer.draw(ctx, {
        t,
        loopSeconds,
        w: canvas.width,
        h: canvas.height,
        audio: features,
        strength,
        quality: 'thumbnail',
      });
    } catch {
      /* a broken renderer must not break the whole grid */
    }
  }

  function loop() {
    if (!running) return;
    const t = ((performance.now() - startedAt) / 1000) % loopSeconds;
    drawOnce(t);
    rafId = requestAnimationFrame(loop);
  }

  function start() {
    if (!animated || !canvas || running || !visible) return;
    running = true;
    startedAt = performance.now();
    rafId = requestAnimationFrame(loop);
  }

  function stop() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
  }

  if (typeof IntersectionObserver !== 'undefined') {
    const observer = new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) start();
        else stop();
      },
      { threshold: 0.05 },
    );
    observer.observe(container);
    container.__observer = observer;
  } else {
    start();
  }

  return {
    element: container,
    start,
    stop,
    redraw: (t = 0) => drawOnce(t),
    dispose() {
      stop();
      container.__observer?.disconnect?.();
      if (video) {
        try {
          video.pause();
        } catch {
          /* ignore */
        }
      }
    },
  };
}

/** Simpler one-shot thumbnail used inside compact list rows. */
export function createStaticThumbnail(scene, { width = 90, height = 160 } = {}) {
  const { renderer } = resolveRenderer(scene, { allowFallback: true });
  if (scene?.assetType === 'video' && scene.assetUrl) {
    return h('div', { class: 'scene-card__thumb', style: { width: `${width}px`, aspectRatio: '9 / 16', flex: 'none' } }, [
      h('video', { src: scene.assetUrl, muted: true, playsInline: true, preload: 'metadata' }),
    ]);
  }
  const canvas = h('canvas', { width, height });
  if (renderer) {
    queueMicrotask(() => {
      const ctx = canvas.getContext('2d');
      renderer.draw(ctx, {
        t: Math.max(2, scene.duration || 10) * 0.35,
        loopSeconds: Math.max(2, scene.duration || 10),
        w: width,
        h: height,
        audio: SILENT_AUDIO,
        strength: 0,
        quality: 'thumbnail',
      });
    });
  }
  return h('div', { class: 'scene-card__thumb', style: { width: `${width}px`, aspectRatio: '9 / 16', flex: 'none' } }, [canvas]);
}
